import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { CurrencyConfigService } from '../../core/currency-config.service';
import { AuthService } from '../auth/auth.service';
import { PublicAd } from '../home/home.service';
import { authenticationRequiredContext } from '../../core/authentication-required.context';

interface ApiResponse {
  code: number;
  message: string;
}

interface FavouriteAdsResponse extends ApiResponse {
  ads: PublicAd[] | null;
}

interface AdResponse extends ApiResponse {
  ad: Pick<PublicAd, 'id' | 'is_favourite'>;
}

@Injectable({
  providedIn: 'root',
})
export class FavouritesService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly currencyConfig = inject(CurrencyConfigService);
  private readonly favouriteState = signal<Record<number, boolean>>({});

  loadFavourites(limit = 24, offset = 0): Observable<FavouriteAdsResponse> {
    const params = new HttpParams()
      .set('limit', String(limit))
      .set('offset', String(offset))
      .set('display_currency', this.currencyConfig.getDisplayCurrencyParam());

    return this.http.get<FavouriteAdsResponse>('/api/favs', {
      params,
      headers: this.buildHeaders(),
      context: authenticationRequiredContext(),
    });
  }

  loadFavouriteState(adId: number): Observable<boolean> {
    return this.http
      .get<AdResponse>(`/api/ad/${adId}`, {
        params: new HttpParams().set('display_currency', this.currencyConfig.getDisplayCurrencyParam()),
        headers: this.buildHeaders(),
      })
      .pipe(
        map((response) => {
          const isFavourite = response.ad?.is_favourite ?? false;
          this.setFavouriteState(adId, isFavourite);
          return isFavourite;
        })
      );
  }

  syncFromAds(ads: Array<Pick<PublicAd, 'id' | 'is_favourite'>>): void {
    this.favouriteState.update((currentState) => {
      const nextState = { ...currentState };

      for (const ad of ads) {
        nextState[ad.id] = ad.is_favourite;
      }

      return nextState;
    });
  }

  resolveState(adId: number, fallback: boolean): boolean {
    const state = this.favouriteState();
    return adId in state ? state[adId] : fallback;
  }

  resetState(): void {
    this.favouriteState.set({});
  }

  toggleFavourite(adId: number, currentValue: boolean): Observable<boolean> {
    if (!this.authService.getApiToken()) {
      return throwError(() => new Error('AUTH_REQUIRED'));
    }

    const nextValue = !currentValue;
    this.setFavouriteState(adId, nextValue);

    const request$ = nextValue
      ? this.http.post<ApiResponse>(
          '/api/fav',
          { ad_id: adId },
          {
            headers: this.buildHeaders(),
            context: authenticationRequiredContext(),
          }
        )
      : this.http.delete<ApiResponse>(`/api/fav/${adId}`, {
        headers: this.buildHeaders(),
        context: authenticationRequiredContext(),
      });

    return request$.pipe(
      map(() => nextValue),
      catchError((error) => {
        this.setFavouriteState(adId, currentValue);
        return throwError(() => error);
      })
    );
  }

  private setFavouriteState(adId: number, value: boolean): void {
    this.favouriteState.update((state) => ({
      ...state,
      [adId]: value,
    }));
  }

  private buildHeaders(): HttpHeaders {
    let headers = new HttpHeaders({ Device: 'web' });
    const token = this.authService.getApiToken();

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }
}
